import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes, useTheme } from "styled-components";
import {
  FiAlertTriangle, FiArrowDownRight, FiArrowUpRight, FiCheck, FiChevronLeft, FiChevronRight, FiDownload,
  FiFileText, FiGrid, FiList, FiMinus, FiPackage, FiSearch, FiTable, FiUpload, FiX,
} from "react-icons/fi";
import { toast } from "react-toastify";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ReferenceDot,
} from "recharts";
import websocketTaskService from "../../../services/websocketTaskService";
import {
  ChipScroll, Chip, Empty, Group, Meter, SearchField, Section, SectionHeader, Skeleton, Widget, WidgetGrid, tokens,
} from "../../activity-logs/styles";
import { SegmentedControl } from "../../activity-logs/components/primitives";
import {
  CreditsPage, DirectoryCard, HeaderLink, Notice, StorageBar, SupplierGrid, Tag, TopHeadline, TopRow, SANS,
} from "../../creditos/styles";
import { BackButton, ScrolledTopBar, SupplierAvatar } from "../../creditos/components/parts";
import { Capsule, PrimaryCapsule, RoundButton, TintedCapsule, Toolbar } from "../../creditos/components/ControlBar";
import { tokens as T, hairline } from "../../../styles/designTokens";
import { initials } from "../../activity-logs/labels";
import NovaAnaliseSheet from "./NovaAnaliseSheet";
import { BANDS, bandOf, buildAnalise, displayName } from "./analiseData";
import {
  branchLabel, conformityColor, formatCnpj, formatCount, formatMonthKey, formatPercent, periodLabel,
} from "./revisorData";

/**
 * Dashboard de uma análise do Revisor SPED.
 * Pergunta da tela: quanto do período tem XML, quem concentra o que falta e por
 * onde começar a cobrar? Elemento próprio: a concentração dos faltantes (poucos
 * fornecedores costumam responder pela maior parte).
 * Dados: /revisor/reports/:id/details (em cache por análise) e a lista de
 * relatórios já carregada, para comparar com a análise anterior.
 */

const API_URL = process.env.REACT_APP_API || "https://api.vidal-app.com";
const STEP = 36;
// Paleta suave: coral no lugar do vermelho saturado; azul e violeta discretos
export const MISS = "#ec7a70";
const NFE = "#6b9cf0";
const CTE = "#a99af0";
export const detailsCache = new Map();
const VIEW_KEY = "vidal_revisor_fornecedores_view";
const readView = () => { try { return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "cards"; } catch { return "cards"; } };
const saveView = (v) => { try { localStorage.setItem(VIEW_KEY, v); } catch { /* preferência só desta sessão */ } };
const VIEW_OPTIONS = [
  { key: "cards", icon: <FiGrid title="Ver em cards" /> },
  { key: "list", icon: <FiList title="Ver em lista" /> },
];

export const authHeaders = () => {
  const auth = JSON.parse(localStorage.getItem("vidal_auth") || "{}");
  const projeto = localStorage.getItem("vidal_selected_project");
  return { ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}), ...(projeto ? { projeto } : {}) };
};
const token = () => JSON.parse(localStorage.getItem("vidal_auth") || "{}").token || "";

// ─── Anel de cobertura ───────────────────────────────────────────────────────

export function Ring({ value, color, size = 132, stroke = 11, label = "com XML" }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(118,118,128,0.18)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.min(100, value) / 100)}
          style={{ transition: "stroke-dashoffset 900ms cubic-bezier(0.2, 0.8, 0.2, 1)" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.04em", lineHeight: 1 }}>
          {value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}<span style={{ fontSize: 15, opacity: 0.5, fontWeight: 600 }}>%</span>
        </span>
        <span style={{ marginTop: 4, fontSize: 11, opacity: 0.55 }}>{label}</span>
      </div>
    </div>
  );
}

export const Hero = styled(Widget)`
  display: flex;
  align-items: center;
  gap: 26px;
  .h-main { flex: 1; min-width: 0; }
  .h-status { display: flex; align-items: center; gap: 7px; font-size: 0.82em; font-weight: 600; letter-spacing: -0.005em; }
  .h-status i { width: 7px; height: 7px; border-radius: 50%; }
  .h-stats { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 8px 30px; font-variant-numeric: tabular-nums; }
  .h-stat { min-width: 0; }
  .h-stat b { display: block; font-size: 1.62em; font-weight: 600; letter-spacing: -0.035em; line-height: 1.1; }
  .h-stat span { display: flex; align-items: center; gap: 6px; margin-top: 4px; font-size: 0.76em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; }
  .h-stat span i { width: 6px; height: 6px; border-radius: 50%; }
  .h-bar { margin-top: 14px; height: 4px; border-radius: 2px; display: flex; overflow: hidden; gap: 2px; background: rgba(118,118,128,0.1); }
  .h-bar span { display: block; height: 100%; border-radius: 2px; }
  .h-note { margin-top: 10px; font-size: 0.8em; font-weight: 300; line-height: 1.5; color: ${({ theme }) => tokens.label2(theme)}; }
  .h-chips { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 6px; }
  .h-chips > span { margin-top: 0; }
  @media (max-width: 640px) { flex-direction: column; align-items: flex-start; .h-main { width: 100%; } }
`;

const COVERAGE_STATUS = {
  full: "Todos com XML", high: "Quase completo", mid: "Cobertura parcial", low: "Cobertura baixa", none: "Nenhum XML",
};

// Resumo de cobertura: situação, números e barra, com a explicação em letra pequena e fina
export function CoverageHero({ pct, color, found, missing, stats, note, chips, $span = 2, $delay = 0 }) {
  const label = missing + found === 0 ? "Sem documentos" : COVERAGE_STATUS[bandOf(pct).key];
  return (
    <Hero $span={$span} $delay={$delay}>
      <Ring value={pct} color={color} size={118} stroke={10} />
      <div className="h-main">
        <div className="h-status"><i style={{ background: color }} />{label}</div>
        <div className="h-stats">
          {stats.map((st) => (
            <div key={st.label} className="h-stat">
              <b>{formatCount(st.value)}</b>
              <span>{st.dot && <i style={{ background: st.dot }} />}{st.label}</span>
            </div>
          ))}
        </div>
        <div className="h-bar" role="img" aria-label={`${formatPercent(pct)} com XML`}>
          <span style={{ flexGrow: found, background: T.brand }} />
          <span style={{ flexGrow: missing, background: MISS }} />
        </div>
        {note && <div className="h-note">{note}</div>}
        {chips.length > 0 && <div className="h-chips">{chips}</div>}
      </div>
    </Hero>
  );
}

export const Delta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 12px;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 0.76em;
  font-weight: 600;
  color: ${({ $tone }) => ($tone === "up" ? T.brand : $tone === "down" ? "#ff453a" : "inherit")};
  background: ${({ $tone }) => ($tone === "up" ? T.brandSoft : $tone === "down" ? "rgba(255,69,58,0.12)" : "rgba(118,118,128,0.14)")};
  svg { stroke-width: 2.6; }
  span { font-weight: 400; opacity: 0.75; margin-left: 3px; color: ${({ theme }) => theme.text}; }
`;

// Faixas: uma barra empilhada e as linhas clicáveis abaixo
export const BandRow = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto 56px 14px;
  align-items: center;
  gap: 10px;
  padding: 9px 2px;
  border: none;
  border-top: 0.5px solid ${({ theme }) => hairline(theme, true)};
  background: none;
  cursor: pointer;
  font-family: ${SANS};
  font-size: 0.84em;
  color: ${({ theme }) => theme.text};
  text-align: left;
  i { width: 8px; height: 8px; border-radius: 50%; }
  .n { font-weight: 600; }
  .m { font-size: 0.9em; color: ${({ theme }) => tokens.label2(theme)}; text-align: right; }
  svg { color: ${({ theme }) => tokens.label3(theme)}; opacity: 0; transition: opacity 140ms ease; }
  &:hover svg { opacity: 1; }
  &:first-of-type { border-top: none; }
`;

const BandHead = styled.div`
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto 56px 14px;
  gap: 10px;
  padding: 0 2px 6px;
  font-size: 0.72em;
  color: ${({ theme }) => tokens.label3(theme)};
  span:nth-child(3), span:nth-child(4) { text-align: right; }
`;

// ─── Exportar ────────────────────────────────────────────────────────────────

const FIELDS = [
  ["chave", "Chave de acesso"], ["nNF", "Número"], ["serie", "Série"], ["dhEmi", "Emissão"], ["cfop", "CFOP"],
  ["valor", "Valor"], ["status", "Status"], ["natOp", "Natureza da operação"], ["emit.xNome", "Emitente"],
  ["emit.CNPJ", "CNPJ do emitente"], ["emit.enderEmit.UF", "UF do emitente"], ["dest.xNome", "Destinatário"],
  ["dest.CNPJ", "CNPJ do destinatário"], ["total.ICMSTot.vICMS", "ICMS"], ["total.ICMSTot.vIPI", "IPI"],
  ["total.ICMSTot.vPIS", "PIS"], ["total.ICMSTot.vCOFINS", "COFINS"], ["total.ICMSTot.vNF", "Valor da nota"],
  ["transp.transporta.xNome", "Transportadora"], ["infAdic.infCpl", "Informações complementares"],
];
const DEFAULT_FIELDS = ["chave", "nNF", "dhEmi", "emit.xNome", "emit.CNPJ", "dest.xNome", "dest.CNPJ", "valor", "status"];

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const rise = keyframes`from { opacity: 0; transform: translateY(14px) scale(0.98); } to { opacity: 1; transform: none; }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: rgba(0, 0, 0, ${({ theme }) => (tokens.dk(theme) ? 0.5 : 0.28)});
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  animation: ${fadeIn} 180ms ease;
  font-family: ${SANS};
`;

const Sheet = styled.div`
  width: min(460px, 100%);
  max-height: min(640px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 24px;
  color: ${({ theme }) => theme.text};
  background: ${({ theme }) => (tokens.dk(theme) ? "#1c1c1e" : "#f2f2f7")};
  box-shadow: 0 0 0 0.5px ${({ theme }) => hairline(theme, true)}, 0 50px 100px -30px rgba(0,0,0,0.5);
  animation: ${rise} 280ms cubic-bezier(0.2, 0.9, 0.25, 1);
  .hd { display: flex; align-items: center; gap: 10px; padding: 16px 16px 8px 20px; }
  .hd h3 { flex: 1; margin: 0; font-size: 1.05em; font-weight: 700; letter-spacing: -0.02em; }
  .bd { padding: 4px 20px 20px; overflow-y: auto; }
  .card { border-radius: 14px; overflow: hidden; background: ${({ theme }) => (tokens.dk(theme) ? "#2c2c2e" : "#fff")}; }
  .opt {
    width: 100%; display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: none; background: none;
    font-family: inherit; color: inherit; text-align: left; cursor: pointer; position: relative;
  }
  .opt + .opt::before { content: ""; position: absolute; top: 0; left: 58px; right: 0; height: 0.5px; background: ${({ theme }) => hairline(theme, true)}; }
  .opt:hover { background: rgba(118,118,128,0.08); }
  .ic { width: 32px; height: 32px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; background: rgba(118,118,128,0.16); color: ${({ theme }) => tokens.label2(theme)}; flex-shrink: 0; }
  .t { font-size: 0.88em; font-weight: 500; }
  .s { font-size: 0.74em; color: ${({ theme }) => tokens.label2(theme)}; margin-top: 1px; }
  .opt > svg { margin-left: auto; color: ${({ theme }) => tokens.label3(theme)}; }
  .lead { margin: 0 0 12px; font-size: 0.82em; color: ${({ theme }) => tokens.label2(theme)}; line-height: 1.45; }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .chip {
    height: 30px; padding: 0 12px; border-radius: 999px; border: none; cursor: pointer; font-family: inherit; font-size: 0.8em; font-weight: 500;
    display: inline-flex; align-items: center; gap: 5px;
    color: ${({ theme }) => theme.text}; background: rgba(118,118,128,0.16);
  }
  .chip.on { color: #fff; background: ${T.brand}; }
  .chip svg { stroke-width: 3; font-size: 12px; }
  .ft { display: flex; align-items: center; gap: 8px; padding: 12px 20px 18px; border-top: 0.5px solid ${({ theme }) => hairline(theme, true)}; }
  .ft .msg { flex: 1; font-size: 0.78em; color: ${({ theme }) => tokens.label2(theme)}; }
`;

function ExportSheet({ reportId, missing, onClose }) {
  const [step, setStep] = useState("menu");
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const open = (path) => { window.open(`${API_URL}/revisor/reports/${reportId}/${path}token=${encodeURIComponent(token())}`, "_blank"); onClose(); };
  const toggle = (k) => setFields((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  return createPortal(
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Sheet role="dialog" aria-modal="true" aria-labelledby="exp-title">
        <div className="hd">
          {step === "excel" && <RoundButton type="button" onClick={() => setStep("menu")} aria-label="Voltar" style={{ width: 30, height: 30 }}><FiChevronLeft /></RoundButton>}
          <h3 id="exp-title">{step === "menu" ? "Exportar" : "Planilha dos faltantes"}</h3>
          <RoundButton type="button" onClick={onClose} aria-label="Fechar" style={{ width: 30, height: 30 }}><FiX /></RoundButton>
        </div>
        {step === "menu" ? (
          <div className="bd">
            <p className="lead">Arquivos dos {formatCount(missing)} documentos sem XML, para cobrar dos fornecedores ou conferir.</p>
            <div className="card">
              <button type="button" className="opt" onClick={() => setStep("excel")}>
                <span className="ic"><FiTable /></span>
                <span><div className="t">Planilha dos faltantes</div><div className="s">Excel, com as colunas que você escolher</div></span>
                <FiChevronRight />
              </button>
              <button type="button" className="opt" onClick={() => open("download-pdfs?")}>
                <span className="ic"><FiFileText /></span>
                <span><div className="t">DANFEs dos faltantes</div><div className="s">PDFs em um arquivo ZIP</div></span>
                <FiDownload />
              </button>
              <button type="button" className="opt" onClick={() => open("download-xmls?")}>
                <span className="ic"><FiPackage /></span>
                <span><div className="t">XMLs da análise</div><div className="s">Arquivo ZIP</div></span>
                <FiDownload />
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="bd">
              <p className="lead">Escolha as colunas. Uma linha por documento faltante.</p>
              <div className="chips">
                {FIELDS.map(([k, label]) => (
                  <button key={k} type="button" className={`chip${fields.includes(k) ? " on" : ""}`} onClick={() => toggle(k)} aria-pressed={fields.includes(k)}>
                    {fields.includes(k) && <FiCheck />}{label}
                  </button>
                ))}
              </div>
            </div>
            <div className="ft">
              <span className="msg">{fields.length} {fields.length === 1 ? "coluna" : "colunas"}</span>
              <Capsule type="button" onClick={() => setFields(fields.length === FIELDS.length ? DEFAULT_FIELDS : FIELDS.map(([k]) => k))}>
                {fields.length === FIELDS.length ? "Padrão" : "Todas"}
              </Capsule>
              <PrimaryCapsule type="button" disabled={fields.length === 0} onClick={() => open(`download-faltantes?fields=${fields.join(",")}&`)}>
                <FiDownload /> Baixar
              </PrimaryCapsule>
            </div>
          </>
        )}
      </Sheet>
    </Overlay>,
    document.body
  );
}

// ─── Histórico de cobertura ───────────────────────────────────────────────────

function HistoryTip({ active, payload }) {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={{
      background: tokens.solid(theme), border: `1px solid ${tokens.separator(theme)}`, borderRadius: 10, padding: "8px 11px",
      fontSize: 12, fontVariantNumeric: "tabular-nums", boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
    }}
    >
      <div style={{ fontWeight: 600 }}>{formatMonthKey(p.key, true)}{p.current ? " · esta análise" : ""}</div>
      <div style={{ marginTop: 3, color: tokens.label2(theme) }}>{formatPercent(p.pct)} com XML · {formatCount(p.faltantes)} faltantes</div>
    </div>
  );
}

function HistoryChart({ data }) {
  const theme = useTheme();
  const cur = data.find((d) => d.current);
  const min = Math.max(0, Math.floor(Math.min(...data.map((d) => d.pct)) / 10) * 10 - 10);
  return (
    <div style={{ marginTop: 10, flex: 1, minHeight: 132 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 8, left: 8, bottom: 0 }}>
          <XAxis
            dataKey="key"
            tickFormatter={(k) => formatMonthKey(k)}
            tick={{ fontSize: 10, fill: tokens.label2(theme) }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis hide domain={[min, 100]} />
          <Tooltip content={<HistoryTip />} cursor={{ stroke: tokens.label3(theme), strokeDasharray: "3 3" }} />
          <Area type="monotone" dataKey="pct" stroke={T.brand} strokeWidth={1.75} fill={T.brand} fillOpacity={0.08} dot={{ r: 2.5, fill: T.brand, strokeWidth: 0 }} activeDot={{ r: 4 }} isAnimationActive />
          {cur && <ReferenceDot x={cur.key} y={cur.pct} r={6} fill={T.brand} stroke={tokens.dk(theme) ? "#1c1c1e" : "#fff"} strokeWidth={2.5} />}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Abrangência ─────────────────────────────────────────────────────────────

const SPED_TYPES = { icms_ipi: "EFD ICMS/IPI", contribuicoes: "EFD Contribuições", pis_cofins: "EFD Contribuições" };

const ScopeLayout = styled.div`
  display: grid;
  grid-template-columns: 290px minmax(0, 1fr);
  gap: 28px;
  margin-top: 16px;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const ScopeSummary = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 28px;
  border-right: 0.5px solid ${({ theme }) => hairline(theme, true)};
  @media (max-width: 860px) { padding-right: 0; border-right: none; }

  .num { font-size: 2.3em; font-weight: 700; letter-spacing: -0.04em; line-height: 1; font-variant-numeric: tabular-nums; }
  .num small { margin-left: 6px; font-size: 0.4em; font-weight: 500; letter-spacing: 0; color: ${({ theme }) => tokens.label2(theme)}; }
  .state { margin-top: 10px; display: flex; align-items: center; gap: 7px; font-size: 0.84em; font-weight: 600; }
  .state i { width: 7px; height: 7px; border-radius: 50%; }
  .strip { margin-top: 12px; display: flex; gap: 3px; }
  .strip span { flex: 1; min-width: 0; height: 6px; border-radius: 3px; }
  .note { margin-top: 12px; font-size: 0.8em; font-weight: 300; line-height: 1.5; color: ${({ theme }) => tokens.label2(theme)}; }
  .facts { margin-top: 12px; display: flex; flex-direction: column; }
  .fact { display: flex; justify-content: space-between; gap: 12px; padding: 7px 0; font-size: 0.8em; border-top: 0.5px solid ${({ theme }) => hairline(theme, true)}; }
  .fact span { color: ${({ theme }) => tokens.label2(theme)}; }
  .fact b { font-weight: 500; text-align: right; }
`;

const ScopeTiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  gap: 8px;
  align-content: start;

  .tile { display: flex; align-items: center; gap: 10px; min-width: 0; padding: 10px 12px; border: none; border-radius: 12px; background: rgba(118,118,128,0.08); color: inherit; font: inherit; text-align: left; }
  .tile.absent { background: transparent; box-shadow: inset 0 0 0 1px ${({ theme }) => hairline(theme, true)}; }
  button.tile { cursor: pointer; transition: background-color ${T.durBase} ${T.easeOut}; }
  button.tile:hover { background: rgba(118,118,128,0.1); }
  button.tile:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }
  .go { flex-shrink: 0; font-size: 0.74em; font-weight: 600; color: ${T.brand}; }
  .ic { flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; font-size: 13px; }
  .ic svg { stroke-width: 2.6; }
  .t { min-width: 0; flex: 1; }
  .t b { display: block; font-size: 0.84em; font-weight: 500; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .t > span { display: block; margin-top: 1px; font-size: 0.74em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .t > span.dots { display: flex; flex-wrap: wrap; gap: 3px; margin-top: 5px; }
  .dots i { width: 7px; height: 7px; border-radius: 2px; }
`;

const SPED_MISSING = "#ff453a";

function scopeNote(s, period) {
  const absent = s.absent.map((e) => branchLabel(e.cnpj));
  const list = absent.length > 3 ? `${absent.slice(0, 3).join(", ")} e mais ${absent.length - 3}` : absent.join(", ");
  if (s.complete) {
    return `Todos os ${formatCount(s.establishments.length)} estabelecimentos conhecidos do projeto têm SPED de ${period}.`;
  }
  return `${s.months.length > 1 ? "SPED incompleto" : "Sem SPED"} em ${period}: ${list}. Os documentos desses SPEDs não entraram nesta conferência, então a cobertura vale só para o que foi enviado.`;
}

function AnaliseScope({ a, onSendMissing }) {
  const theme = useTheme();
  const s = a.scope;
  const multi = s.months.length > 1;
  const stateColor = s.complete ? T.brand : MISS;
  const stateLabel = s.complete
    ? "Todos os SPEDs enviados"
    : `${s.missing === 1 ? "Falta" : "Faltam"} ${formatCount(s.missing)} ${s.missing === 1 ? "SPED" : "SPEDs"}`;
  const types = s.types.map((t) => SPED_TYPES[t] || t).join(", ");
  return (
    <ScopeLayout>
      <ScopeSummary>
        <div className="num">{formatCount(s.sent)}<small>de {formatCount(s.expected)} SPEDs</small></div>
        <div className="state"><i style={{ background: stateColor }} />{stateLabel}</div>
        <div className="strip" role="img" aria-label={`${formatCount(s.sent)} de ${formatCount(s.expected)} SPEDs enviados`}>
          {s.cells.map((c) => (
            <span
              key={`${c.cnpj}-${c.month}`}
              title={`${branchLabel(c.cnpj)} · ${formatMonthKey(c.month)}: ${c.sent ? (c.docs > 0 ? "enviado" : "enviado, sem movimento") : "não enviado"}`}
              style={{ background: c.sent ? T.brand : SPED_MISSING, opacity: c.sent && c.docs === 0 ? 0.4 : 1 }}
            />
          ))}
        </div>
        <div className="note">{scopeNote(s, periodLabel(a.rep))}</div>
        {!s.complete && (
          <TintedCapsule type="button" onClick={onSendMissing} style={{ marginTop: 12, alignSelf: "flex-start" }}>
            <FiUpload /> Enviar {s.missing === 1 ? "o SPED que falta" : `os ${formatCount(s.missing)} SPEDs que faltam`}
          </TintedCapsule>
        )}
        <div className="facts">
          <div className="fact"><span>Período</span><b>{periodLabel(a.rep)}</b></div>
          {types && <div className="fact"><span>Tipo</span><b>{types}</b></div>}
          <div className="fact"><span>Sem movimento</span><b>{s.idle.length === 0 ? "nenhum" : s.idle.map((e) => branchLabel(e.cnpj)).join(", ")}</b></div>
        </div>
      </ScopeSummary>

      <ScopeTiles>
        {s.establishments.map((e) => {
          const absent = e.state === "missing";
          const canSend = absent || e.state === "partial";
          const TileTag = canSend ? "button" : "div";
          let icon = <FiCheck />; let color = T.brand; let tint = T.brandSoft;
          if (e.state === "idle") { icon = <FiMinus />; color = tokens.label2(theme); tint = "rgba(118,118,128,0.16)"; }
          if (e.state === "missing") { icon = <FiX />; color = MISS; tint = `${MISS}1f`; }
          if (e.state === "partial") { icon = <FiMinus />; color = "#ffa61a"; tint = "#ffa61a1f"; }
          let sub = `${formatCount(e.docs)} documentos`;
          if (e.state === "idle") sub = "Sem movimento";
          if (absent) sub = "SPED não enviado";
          if (multi && !absent) sub = `${formatCount(e.sentCount)} de ${formatCount(s.months.length)} meses`;
          return (
            <TileTag
              key={e.cnpj}
              {...(canSend ? { type: "button", onClick: onSendMissing } : {})}
              className={`tile${absent ? " absent" : ""}`}
              title={canSend ? `Enviar o SPED de ${branchLabel(e.cnpj)}` : formatCnpj(e.cnpj)}
            >
              <span className="ic" style={{ color, background: tint }}>{icon}</span>
              <span className="t">
                <b>{branchLabel(e.cnpj)}</b>
                <span>{sub}</span>
                {multi && (
                  <span className="dots">
                    {e.cells.map((c) => <i key={c.month} title={`${formatMonthKey(c.month)}: ${c.sent ? "enviado" : "não enviado"}`} style={{ background: c.sent ? T.brand : SPED_MISSING }} />)}
                  </span>
                )}
              </span>
              {canSend && <span className="go">Enviar</span>}
            </TileTag>
          );
        })}
      </ScopeTiles>
    </ScopeLayout>
  );
}

// ─── Estabelecimentos ────────────────────────────────────────────────────────

const EstLayout = styled.div`
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr);
  gap: 28px;
  margin-top: 16px;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const EstSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 28px;
  border-right: 0.5px solid ${({ theme }) => hairline(theme, true)};
  @media (max-width: 860px) { padding-right: 0; border-right: none; }

  .donut { position: relative; width: 150px; height: 150px; align-self: center; }
  .donut .c { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; pointer-events: none; }
  .donut .c b { font-size: 22px; font-weight: 700; letter-spacing: -0.03em; }
  .donut .c span { font-size: 11px; color: ${({ theme }) => tokens.label2(theme)}; }
  .facts { display: flex; flex-direction: column; }
  .fact { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; padding: 8px 0; font-size: 0.82em; border-top: 0.5px solid ${({ theme }) => hairline(theme, true)}; }
  .fact:first-child { border-top: none; }
  .fact span { color: ${({ theme }) => tokens.label2(theme)}; display: inline-flex; align-items: center; gap: 7px; }
  .fact i { width: 8px; height: 8px; border-radius: 2px; display: inline-block; }
  .fact b { font-weight: 600; }
`;

const EstBars = styled.div`
  display: flex;
  flex-direction: column;
  .hd, .row { display: grid; grid-template-columns: 150px minmax(0, 1fr) 64px 52px; align-items: center; gap: 14px; }
  .hd { padding-bottom: 8px; font-size: 0.72em; color: ${({ theme }) => tokens.label3(theme)}; }
  .hd span:nth-child(n+3) { text-align: right; }
  &.with-missing .hd, &.with-missing .row { grid-template-columns: 150px minmax(0, 1fr) 64px 64px 52px; }
  .row { padding: 7px 0; font-size: 0.84em; border-radius: 8px; transition: background-color 140ms ease; }
  .row:hover { background: rgba(118,118,128,0.07); }
  .nm { display: flex; align-items: baseline; gap: 7px; min-width: 0; white-space: nowrap; }
  .nm b { font-weight: 500; }
  .nm span { font-size: 0.86em; color: ${({ theme }) => tokens.label3(theme)}; }
  .track { position: relative; height: 10px; border-radius: 5px; background: rgba(118,118,128,0.1); overflow: hidden; display: flex; }
  .track span { height: 100%; transition: width 700ms cubic-bezier(0.2, 0.8, 0.2, 1); }
  .track span:first-child { border-radius: 5px 0 0 5px; }
  .track span:last-child { border-radius: 0 5px 5px 0; }
  .bars { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .miss-track { height: 5px; border-radius: 3px; background: rgba(118,118,128,0.1); overflow: hidden; }
  .miss-track span { display: block; height: 100%; border-radius: 3px; background: ${MISS}; transition: width 700ms cubic-bezier(0.2, 0.8, 0.2, 1); }
  .idle { font-size: 0.86em; color: ${({ theme }) => tokens.label3(theme)}; }
  .v { text-align: right; font-weight: 600; }
  .m { text-align: right; font-weight: 600; color: ${MISS}; }
  .m.zero { font-weight: 400; color: ${({ theme }) => tokens.label3(theme)}; }
  .p { text-align: right; font-size: 0.9em; color: ${({ theme }) => tokens.label2(theme)}; }
  @media (max-width: 560px) {
    .hd, .row { grid-template-columns: 110px minmax(0, 1fr) 56px; }
    &.with-missing .hd, &.with-missing .row { grid-template-columns: 96px minmax(0, 1fr) 50px 50px; }
    .hd span:last-child, .p { display: none; }
  }
`;

function Establishments({ a }) {
  const theme = useTheme();
  const top = a.estRanked[0];
  const max = Math.max(1, top?.docs || 0);
  const showMissing = a.estMissingReady;
  const topMissing = showMissing ? [...a.estRanked].sort((p, q) => q.missing - p.missing)[0] : null;
  const maxMissing = Math.max(1, topMissing?.missing || 0);
  const pie = [{ k: "NF-e", v: a.estNfes, c: NFE }, { k: "CT-e", v: a.estCtes, c: CTE }].filter((x) => x.v > 0);
  return (
    <>
      <EstLayout>
        <EstSummary>
          <div className="donut">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="v" innerRadius={54} outerRadius={68} paddingAngle={pie.length > 1 ? 3 : 0} cornerRadius={4} stroke="none" startAngle={90} endAngle={-270}>
                  {pie.map((x) => <Cell key={x.k} fill={x.c} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="c"><b>{formatCount(a.estDocs)}</b><span>documentos</span></div>
          </div>
          <div className="facts">
            <div className="fact"><span><i style={{ background: NFE }} />NF-e</span><b>{formatCount(a.estNfes)} <small style={{ fontWeight: 400, color: tokens.label2(theme) }}>{formatPercent(a.estDocs ? (a.estNfes / a.estDocs) * 100 : 0)}</small></b></div>
            <div className="fact"><span><i style={{ background: CTE }} />CT-e</span><b>{formatCount(a.estCtes)} <small style={{ fontWeight: 400, color: tokens.label2(theme) }}>{formatPercent(a.estDocs ? (a.estCtes / a.estDocs) * 100 : 0)}</small></b></div>
            {top && <div className="fact"><span>Maior volume</span><b>{branchLabel(top.cnpj)} · {formatPercent(a.estDocs ? (top.docs / a.estDocs) * 100 : 0)}</b></div>}
            {topMissing?.missing > 0 && <div className="fact"><span>Mais faltantes</span><b>{branchLabel(topMissing.cnpj)} · {formatCount(topMissing.missing)}</b></div>}
            <div className="fact"><span>Sem movimento</span><b>{a.estIdle.length === 0 ? "nenhum" : a.estIdle.map((e) => branchLabel(e.cnpj)).join(", ")}</b></div>
          </div>
        </EstSummary>

        <EstBars className={showMissing ? "with-missing" : undefined}>
          <div className="hd"><span>Estabelecimento</span><span>{showMissing ? "NF-e e CT-e no SPED · faltantes" : "NF-e e CT-e no SPED"}</span><span>Docs</span>{showMissing && <span>Faltantes</span>}<span>Parte</span></div>
          {a.estRanked.map((e) => (
            <div key={e.cnpj} className="row" title={`${formatCnpj(e.cnpj)} · ${e.file || ""}`}>
              <span className="nm"><b>{branchLabel(e.cnpj)}</b><span>{formatCnpj(e.cnpj).slice(11)}</span></span>
              <span className="bars">
                {e.docs === 0 ? <span className="idle">Sem documentos no SPED</span> : (
                  <span className="track" title={`${formatCount(e.nfes)} NF-e · ${formatCount(e.ctes)} CT-e`}>
                    <span style={{ width: `${(e.nfes / max) * 100}%`, background: NFE }} />
                    <span style={{ width: `${(e.ctes / max) * 100}%`, background: CTE }} />
                  </span>
                )}
                {showMissing && (
                  <span className="miss-track" title={`${formatCount(e.missing)} faltantes`}>
                    <span style={{ width: `${(e.missing / maxMissing) * 100}%` }} />
                  </span>
                )}
              </span>
              <span className="v">{formatCount(e.docs)}</span>
              {showMissing && <span className={`m${e.missing === 0 ? " zero" : ""}`}>{formatCount(e.missing)}</span>}
              <span className="p">{formatPercent(a.estDocs ? (e.docs / a.estDocs) * 100 : 0)}</span>
            </div>
          ))}
        </EstBars>
      </EstLayout>
      <div className="w-sub" style={{ marginTop: 14 }}>
        Contagem lida de cada SPED ({formatCount(a.estDocs)} documentos), diferente dos {formatCount(a.docs)} conferidos no resumo. {showMissing ? `A barra coral compara os ${formatCount(a.missing)} faltantes entre os estabelecimentos.` : "Os faltantes por estabelecimento ainda não vêm separados da API."}
      </div>
    </>
  );
}

// ─── Card de fornecedor ──────────────────────────────────────────────────────

const SupplierCard = React.memo(function SupplierCard({ s, delay, onOpen }) {
  const color = conformityColor(s.pct);
  return (
    <DirectoryCard type="button" $delay={delay} onClick={() => onOpen(s)}>
      <span className="head">
        <SupplierAvatar name={s.name} size={40} />
        <span className="who">
          <span className="name" title={s.name}>{displayName(s.name)}</span>
          <span className="id">{s.cnpj || "Sem CNPJ"}</span>
        </span>
      </span>
      <span className="body">
        <span className="amount">
          {formatPercent(s.pct)}
          <span style={{ fontSize: "0.5em", fontWeight: 500, opacity: 0.5, marginLeft: 6, letterSpacing: 0 }}>com XML</span>
        </span>
        <span className="meta">{formatCount(s.encontradas)} de {formatCount(s.total)} documentos</span>
        <Meter $color={color} className="bar" style={{ marginTop: 12 }}><div style={{ width: `${s.pct}%` }} /></Meter>
        <span className="taxes">
          {s.faltantes > 0 ? <Tag $color="#ef4444">{formatCount(s.faltantes)} faltantes</Tag> : <Tag $color={T.brand}>Sem pendência</Tag>}
        </span>
      </span>
    </DirectoryCard>
  );
});

// ─── Linha de fornecedor (visualização em lista) ─────────────────────────────

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  > :first-child { flex: 1; min-width: 0; margin-top: 0; padding: 2px; }
  > :last-child { flex-shrink: 0; }
`;

// Lista agrupada do iOS. O anel em volta das iniciais é a cobertura de XML e a
// única cor forte da linha; a seta só aparece no hover e no foco.
const ringGrow = keyframes`from { stroke-dasharray: 0 100; }`;
// Mesmas faixas de BANDS, com a saturação da paleta do iOS: o anel é pequeno e pode ser vivo
export const RING_COLORS = { full: "#10d98f", high: "#2dd4bf", mid: "#ffa61a", low: "#ff5c4d", none: "#ff453a" };

const SupplierRows = styled(Group)`
  margin-top: 14px;
  padding: 2px 6px 6px;
  font-variant-numeric: tabular-nums;

  .hd, .row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 76px 112px 112px 14px;
    align-items: center;
    column-gap: 14px;
  }
  .hd {
    padding: 11px 10px 7px;
    font-size: 0.72em;
    color: ${({ theme }) => tokens.label3(theme)};
  }
  .hd .num { text-align: right; }

  .row {
    position: relative;
    width: 100%;
    padding: 9px 10px;
    border: none;
    border-radius: 12px;
    background: transparent;
    color: ${({ theme }) => theme.text};
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background-color ${T.durBase} ${T.easeOut};
  }
  /* Separador alinhado ao texto, como nas listas do iOS */
  .row + .row::before {
    content: "";
    position: absolute;
    top: 0;
    left: 64px;
    right: 10px;
    height: 1px;
    background: ${({ theme }) => hairline(theme)};
    opacity: 0.7;
    transition: opacity ${T.durBase} ${T.easeOut};
  }
  .row:hover, .row:focus-visible { background: rgba(118,118,128,0.08); }
  .row:active { background: rgba(118,118,128,0.14); }
  .row:hover::before, .row:hover + .row::before,
  .row:focus-visible::before, .row:focus-visible + .row::before { opacity: 0; }
  .row:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }

  .who { display: flex; align-items: center; gap: 12px; min-width: 0; }
  .ring { position: relative; flex-shrink: 0; width: 42px; height: 42px; }
  .ring svg { display: block; width: 100%; height: 100%; transform: rotate(-90deg); }
  .ring circle { fill: none; stroke-width: 3; }
  .ring .rail { stroke: rgba(118,118,128,0.16); }
  .ring .arc { stroke-linecap: round; animation: ${ringGrow} 760ms ${T.easeOut} both; }
  @media (prefers-reduced-motion: reduce) { .ring .arc { animation: none; } }
  .ring .ini {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.68em;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: ${({ theme }) => tokens.label2(theme)};
  }
  .txt { display: flex; flex-direction: column; min-width: 0; }
  .name { font-size: 0.92em; font-weight: 500; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .id { margin-top: 2px; font-size: 0.78em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  .pct { text-align: right; font-size: 0.94em; font-weight: 500; letter-spacing: -0.015em; }
  .pct small { margin-left: 1px; font-size: 0.74em; font-weight: 500; opacity: 0.5; }
  .docs { text-align: right; font-size: 0.8em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; }
  .miss { display: flex; align-items: center; justify-content: flex-end; gap: 7px; font-size: 0.94em; font-weight: 500; letter-spacing: -0.015em; }
  .miss i { width: 6px; height: 6px; border-radius: 50%; background: ${MISS}; }
  .miss.clear { font-size: 0.8em; font-weight: 400; letter-spacing: 0; color: ${({ theme }) => tokens.label3(theme)}; }
  .miss.clear svg { font-size: 1.05em; color: ${T.brand}; }

  .chevron { font-size: 15px; opacity: 0; color: ${({ theme }) => tokens.label3(theme)}; transition: opacity ${T.durBase} ${T.easeOut}, transform ${T.durBase} ${T.easeOut}; transform: translateX(-3px); }
  .row:hover .chevron, .row:focus-visible .chevron { opacity: 1; transform: none; }

  @media (max-width: 720px) {
    .hd { display: none; }
    .row { grid-template-columns: minmax(0, 1fr) auto; grid-template-areas: "who pct" "who miss"; column-gap: 10px; row-gap: 2px; }
    .row + .row::before { left: 56px; }
    .who { grid-area: who; gap: 10px; }
    .ring { width: 36px; height: 36px; }
    .ring .ini { font-size: 0.6em; }
    .pct { grid-area: pct; }
    .miss { grid-area: miss; font-size: 0.8em; color: ${({ theme }) => tokens.label2(theme)}; }
    .docs, .chevron, .miss.clear { display: none; }
    .row:has(.miss.clear) { grid-template-areas: "who pct"; }
  }
`;

const SupplierRow = React.memo(function SupplierRow({ s, onOpen }) {
  const pct = formatPercent(s.pct);
  return (
    <button type="button" className="row" onClick={() => onOpen(s)}>
      <span className="who">
        <span className="ring" aria-hidden="true">
          <svg viewBox="0 0 42 42">
            <circle className="rail" cx="21" cy="21" r="19" />
            {s.pct > 0 && <circle className="arc" cx="21" cy="21" r="19" pathLength="100" stroke={RING_COLORS[bandOf(s.pct).key]} style={{ strokeDasharray: `${s.pct} 100` }} />}
          </svg>
          <span className="ini">{initials(s.name)}</span>
        </span>
        <span className="txt">
          <span className="name" title={s.name}>{displayName(s.name)}</span>
          <span className="id">{s.cnpj || "Sem CNPJ"}</span>
        </span>
      </span>
      <span className="pct">{pct.replace("%", "")}<small>%</small></span>
      <span className="docs">{formatCount(s.encontradas)} de {formatCount(s.total)}</span>
      {s.faltantes > 0
        ? <span className="miss"><i />{formatCount(s.faltantes)}</span>
        : <span className="miss clear" title="Sem pendência"><FiCheck /><span>Sem pendência</span></span>}
      <FiChevronRight className="chevron" />
    </button>
  );
});

// ─── Tela ────────────────────────────────────────────────────────────────────

export default function AnaliseDashboard({ report, reports = [], onBack, onOpenSupplier, onReprocessStart, onReprocessFinished }) {
  const theme = useTheme();
  const reportId = report.id || report._id;
  const [details, setDetails] = useState(() => detailsCache.get(reportId) || null);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("");
  const [sort, setSort] = useState("missing");
  const [visible, setVisible] = useState(STEP);
  const [view, setView] = useState(readView);
  const [sendingMissing, setSendingMissing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [byGroup, setByGroup] = useState(false);
  const [downloadPct, setDownloadPct] = useState(null);
  const [taskPct, setTaskPct] = useState(null);
  const listRef = useRef(null);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await fetch(`${API_URL}/revisor/reports/${reportId}/details`, { headers: authHeaders() });
      const data = await res.json();
      if (!data.success) throw new Error("Resposta sem sucesso");
      const d = { suppliers: data.data.suppliers || [], companies: data.data.companies || [] };
      detailsCache.set(reportId, d);
      setDetails(d);
    } catch (err) {
      console.error("[Revisor] detalhes da análise:", err);
      setFailed(true);
    }
  }, [reportId]);

  useEffect(() => { if (!detailsCache.has(reportId)) load(); }, [reportId, load]);

  // Processamento em andamento (outra análise sendo gerada)
  useEffect(() => websocketTaskService.subscribe((all) => {
    const running = all.find((t) => t.type === "process_speds_revisor" && t.status === "running");
    setTaskPct(running ? running.progress ?? 0 : null);
  }), []);

  const a = useMemo(() => buildAnalise(report, details, reports), [report, details, reports]);
  const openSupplierRow = useCallback((s) => onOpenSupplier(s.raw), [onOpenSupplier]);
  const loading = !details && !failed;

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    const digits = term.replace(/\D/g, "");
    let l = a.suppliers.filter((s) => (!band || s.band === band || (band === "pending" && s.faltantes > 0))
      && (!term || s.name.toLowerCase().includes(term) || (digits && s.cnpj.replace(/\D/g, "").includes(digits))));
    if (sort === "missing") l = [...l].sort((x, y) => y.faltantes - x.faltantes || x.pct - y.pct);
    else if (sort === "pct") l = [...l].sort((x, y) => x.pct - y.pct || y.faltantes - x.faltantes);
    else l = [...l].sort((x, y) => x.name.localeCompare(y.name));
    return l;
  }, [a.suppliers, q, band, sort]);

  const hasMore = visible < list.length;
  useEffect(() => {
    const el = endRef.current;
    if (!el || !hasMore) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible((v) => v + STEP); }, { rootMargin: "800px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, list.length, visible]);

  const goToList = (nextBand) => {
    setBand(nextBand);
    setVisible(STEP);
    setTimeout(() => listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
  };

  const downloadAll = async () => {
    setDownloadPct(0);
    try {
      const res = await fetch(`${API_URL}/revisor/reports/${reportId}/download-all`, { headers: authHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Erro ao baixar o relatório");
      }
      const size = Number(res.headers.get("Content-Length")) || 0;
      const reader = res.body.getReader();
      const chunks = [];
      let got = 0;
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        got += value.length;
        if (size) setDownloadPct(Math.round((got / size) * 100));
      }
      const url = URL.createObjectURL(new Blob(chunks, { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `revisor-${a.rep.startKey || reportId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[Revisor] download:", err);
      toast.error(err.message);
    } finally {
      setDownloadPct(null);
    }
  };

  const color = conformityColor(a.pct);
  let deltaTone = "flat";
  if (a.delta != null && a.delta > 0.05) deltaTone = "up";
  if (a.delta != null && a.delta < -0.05) deltaTone = "down";
  const nSup = a.suppliers.length;
  const missingTotal = Math.max(1, a.supplierMissing);
  const zipSize = report.zipSize ? `${(report.zipSize / 1048576).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB` : null;

  return (
    <CreditsPage>
      <ScrolledTopBar scrollToTop>
        <div className="title-row">
          <div>
            <BackButton onClick={onBack}>Revisor SPED</BackButton>
            <h1>{periodLabel(a.rep)}</h1>
            <div className="meta">
              <span className="update-tag">{formatCount(a.rep.speds)} {a.rep.speds === 1 ? "SPED" : "SPEDs"} · {formatCount(a.rep.companies || a.establishments.length)} empresas</span>
              <span className="update-tag">criada em {new Date(report.createdAt).toLocaleDateString("pt-BR")}</span>
            </div>
          </div>
        </div>
        <div className="nav-row">
          <span style={{ fontSize: "0.84em", color: tokens.label2(theme) }}>Documentos escriturados nos SPEDs conferidos com os XMLs do projeto</span>
          <Toolbar>
            <Capsule type="button" onClick={() => setExporting(true)} disabled={a.missing === 0}><FiTable /> Exportar faltantes</Capsule>
            <PrimaryCapsule type="button" onClick={downloadAll} disabled={downloadPct !== null} title={zipSize ? `Relatório completo · ${zipSize}` : "Relatório completo"}>
              <FiDownload /> {downloadPct !== null ? `Baixando ${downloadPct}%` : "Baixar relatório"}
            </PrimaryCapsule>
          </Toolbar>
        </div>
      </ScrolledTopBar>

      {taskPct !== null && (
        <Section $first>
          <Notice style={{ marginTop: 0 }}>
            <FiAlertTriangle />
            <span>Outra análise está sendo processada ({Math.round(taskPct)}%). Esta continua disponível normalmente.</span>
          </Notice>
        </Section>
      )}

      {/* Resumo */}
      <Section $first={taskPct === null}>
        <WidgetGrid>
          <CoverageHero
            pct={a.pct}
            color={color}
            found={a.found}
            missing={a.missing}
            stats={[
              { label: "Com XML", value: a.found, dot: T.brand },
              { label: "Documentos", value: a.docs },
              { label: a.rep.speds === 1 ? "SPED" : "SPEDs", value: a.rep.speds },
            ]}
            note={`${formatCount(a.found)} de ${formatCount(a.docs)} documentos escriturados têm XML no projeto. ${a.missing > 0
              ? `Faltam ${formatCount(a.missing)} XMLs${a.withMissing ? `, espalhados por ${formatCount(a.withMissing)} fornecedores` : ""}.`
              : "Nenhum documento sem XML neste período."}`}
            chips={[
              a.previous && (
                <Delta key="delta" $tone={deltaTone}>
                  {deltaTone === "up" && <FiArrowUpRight />}
                  {deltaTone === "down" && <FiArrowDownRight />}
                  {a.delta > 0 ? "+" : ""}{a.delta.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} p.p.
                  <span>vs. {formatMonthKey(a.previous.startKey)}</span>
                </Delta>
              ),
              a.pareto > 0 && <Delta key="pareto" $tone="flat">80% dos faltantes<span>em {formatCount(a.pareto)} {a.pareto === 1 ? "fornecedor" : "fornecedores"}</span></Delta>,
            ].filter(Boolean)}
          />

          <Widget $delay={50} $clickable onClick={() => goToList("pending")}>
            <div className="w-head"><FiAlertTriangle style={{ color: MISS, opacity: 1 }} /> XMLs faltantes</div>
            <div className="w-value">{formatCount(a.missing)}</div>
            <div className="w-foot" style={{ flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
              <span>{loading ? "…" : `em ${formatCount(a.withMissing)} de ${formatCount(nSup)} fornecedores`}</span>
              {a.previous && (
                <Delta $tone={a.missing < a.previous.faltantes ? "up" : a.missing > a.previous.faltantes ? "down" : "flat"} style={{ marginTop: 0 }}>
                  {a.missing <= a.previous.faltantes ? <FiArrowDownRight /> : <FiArrowUpRight />}
                  {a.missing - a.previous.faltantes > 0 ? "+" : ""}{formatCount(a.missing - a.previous.faltantes)}
                  <span>vs. {formatMonthKey(a.previous.startKey)} ({formatCount(a.previous.faltantes)})</span>
                </Delta>
              )}
            </div>
          </Widget>

          <Widget $delay={100} $clickable onClick={() => goToList("full")}>
            <div className="w-head"><FiCheck style={{ color: T.brand, opacity: 1 }} /> Fornecedores sem pendência</div>
            <div className="w-value">{loading ? "…" : formatCount(a.clean)}</div>
            <div className="w-foot" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
              <span>{loading ? "" : `${formatPercent(nSup ? (a.clean / nSup) * 100 : 0)} dos ${formatCount(nSup)} fornecedores`}</span>
              {!loading && <Meter $color={T.brand}><div style={{ width: `${nSup ? (a.clean / nSup) * 100 : 0}%` }} /></Meter>}
            </div>
          </Widget>
        </WidgetGrid>
      </Section>

      {/* Abrangência: quais SPEDs entraram na análise */}
      {a.scope.expected > 0 && (
        <Section $delay={60}>
          <WidgetGrid>
            <Widget $span={4} $delay={120}>
              <div className="w-title">Abrangência da análise</div>
              <div className="w-sub">Quais SPEDs entraram nesta análise, entre os estabelecimentos conhecidos pelas análises do projeto</div>
              <AnaliseScope a={a} onSendMissing={() => setSendingMissing(true)} />
            </Widget>
          </WidgetGrid>
        </Section>
      )}

      {/* Onde estão os faltantes + faixas de cobertura */}
      <Section $delay={80}>
        <WidgetGrid $cols={2}>
          <Widget $delay={150}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <div className="w-title">Onde estão os faltantes</div>
                <div className="w-sub">Por onde começar a pedir os XMLs</div>
              </div>
              <SegmentedControl
                small
                value={byGroup ? "group" : "cnpj"}
                onChange={(val) => setByGroup(val === "group")}
                options={[{ key: "cnpj", label: "CNPJ" }, { key: "group", label: "Grupo" }]}
              />
            </div>
            {loading && <div style={{ marginTop: 16 }}><Skeleton $h={240} $r={12} /></div>}
            {!loading && a.topMissing.length === 0 && <div className="w-sub" style={{ marginTop: 16 }}>Nenhum fornecedor com pendência.</div>}
            {!loading && a.topMissing.length > 0 && (
              <>
                <TopHeadline>
                  <span className="n">{formatPercent((byGroup ? a.topGroupShare : a.topShare) * 100).replace("%", "")}<span className="pct">%</span></span>
                  <span className="l">dos faltantes estão em {(byGroup ? a.topGroups : a.topMissing).length} {byGroup ? "grupos" : "fornecedores"}</span>
                </TopHeadline>
                <StorageBar role="img" aria-label={`${formatPercent(a.topShare * 100)} dos faltantes em ${a.topMissing.length} fornecedores`}>
                  {(byGroup ? a.topGroups : a.topMissing).map((s, i) => (
                    <span key={s.key} title={`${displayName(s.name)}: ${formatCount(s.faltantes)}`} style={{ flexGrow: s.faltantes, background: MISS, opacity: Math.max(0.3, 0.95 - i * 0.075) }} />
                  ))}
                  <span className="rest" style={{ flexGrow: Math.max(0, a.supplierMissing - (byGroup ? a.topGroups : a.topMissing).reduce((x, s) => x + s.faltantes, 0)) }} />
                </StorageBar>
                <div style={{ marginTop: 8 }}>
                  {byGroup && a.topGroups.map((g) => (
                    <TopRow key={g.key} type="button" onClick={() => onOpenSupplier(g.lead.raw)} title={`Abre o CNPJ com mais faltantes: ${g.lead.cnpj}`}>
                      <span className="lead"><SupplierAvatar name={g.name} size={32} /></span>
                      <span className="main">
                        <span className="title" title={g.name}>{displayName(g.name)}</span>
                        <span className="sub">{g.own ? "Próprio grupo · " : ""}{formatPercent(g.pct)} com XML · {g.members.length} {g.members.length === 1 ? "CNPJ" : "CNPJs"}</span>
                      </span>
                      <span className="end">
                        <span className="value">{formatCount(g.faltantes)}</span>
                        <span className="share">{formatPercent((g.faltantes / missingTotal) * 100)}</span>
                      </span>
                      <FiChevronRight className="chevron" />
                    </TopRow>
                  ))}
                  {!byGroup && a.topMissing.map((s) => (
                    <TopRow key={s.key} type="button" onClick={() => onOpenSupplier(s.raw)}>
                      <span className="lead"><SupplierAvatar name={s.name} size={32} /></span>
                      <span className="main">
                        <span className="title" title={s.name}>{displayName(s.name)}</span>
                        <span className="sub">{formatPercent(s.pct)} com XML · {s.cnpj}</span>
                      </span>
                      <span className="end">
                        <span className="value">{formatCount(s.faltantes)}</span>
                        <span className="share">{formatPercent((s.faltantes / missingTotal) * 100)}</span>
                      </span>
                      <FiChevronRight className="chevron" />
                    </TopRow>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 10 }}>
                  <span className="w-sub" style={{ margin: 0 }}>
                    {byGroup
                      ? (a.ownMissing > 0
                        ? <><b>{formatCount(a.ownMissing)}</b> faltantes ({formatPercent((a.ownMissing / missingTotal) * 100)}) são notas entre os próprios estabelecimentos: XMLs que a empresa já deveria ter.</>
                        : <>{formatCount(a.groupCount)} grupos com pendência, somando os CNPJs de cada um.</>)
                      : a.pareto > 0 && <>80% dos faltantes estão em <b>{formatCount(a.pareto)}</b> dos {formatCount(a.withMissing)} fornecedores.</>}
                  </span>
                  <HeaderLink type="button" onClick={() => { setSort("missing"); goToList("pending"); }}>Ver todos <FiChevronRight /></HeaderLink>
                </div>
              </>
            )}
          </Widget>

          <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <Widget $delay={200}>
            <div className="w-title">Fornecedores por cobertura</div>
            <div className="w-sub">Parte dos documentos de cada fornecedor que tem XML</div>
            {loading ? <div style={{ marginTop: 16 }}><Skeleton $h={240} $r={12} /></div> : (
              <>
                <TopHeadline>
                  <span className="n">{formatCount(a.bands.find((b) => b.key === "none").count)}</span>
                  <span className="l">fornecedores sem nenhum XML no período</span>
                </TopHeadline>
                <StorageBar role="img" aria-label="Distribuição dos fornecedores por cobertura">
                  {a.bands.filter((b) => b.count > 0).map((b) => (
                    <span key={b.key} title={`${b.label}: ${b.count}`} style={{ flexGrow: b.count, background: b.color }} />
                  ))}
                </StorageBar>
                <div style={{ marginTop: 12 }}>
                  <BandHead><span /><span>Faixa</span><span>Fornecedores</span><span>Faltantes</span><span /></BandHead>
                  {a.bands.map((b) => (
                    <BandRow key={b.key} type="button" onClick={() => goToList(b.key)} disabled={b.count === 0} style={{ opacity: b.count === 0 ? 0.45 : 1 }}>
                      <i style={{ background: b.color }} />
                      <span>{b.label}</span>
                      <span className="n">{formatCount(b.count)}</span>
                      <span className="m">{b.missing > 0 ? `${formatPercent((b.missing / missingTotal) * 100)}` : "—"}</span>
                      <FiChevronRight />
                    </BandRow>
                  ))}
                </div>
                {a.valueShare != null && (
                  <div className="w-sub" style={{ marginTop: 10 }}>
                    Fornecedores com pendência respondem por <b>{formatPercent(a.valueShare)}</b> do valor dos documentos.
                  </div>
                )}
              </>
            )}
          </Widget>

          {a.history.length > 1 && (
            <Widget $delay={240} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div>
                  <div className="w-title">Histórico de cobertura</div>
                  <div className="w-sub">Documentos com XML em cada análise do projeto</div>
                </div>
                <span className="w-sub" style={{ whiteSpace: "nowrap" }}>{a.history.length} análises</span>
              </div>
              <HistoryChart data={a.history} />
            </Widget>
          )}
          </div>
        </WidgetGrid>
      </Section>

      {/* Estabelecimentos */}
      {a.establishments.length > 0 && (
        <Section $delay={140}>
          <WidgetGrid>
            <Widget $span={4} $delay={250}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div className="w-title">Estabelecimentos</div>
                  <div className="w-sub">NF-e e CT-e lidos de cada SPED do período</div>
                </div>
                <span className="w-sub">{formatCount(a.establishments.length)} SPEDs</span>
              </div>
              <Establishments a={a} />
            </Widget>
          </WidgetGrid>
        </Section>
      )}

      {/* Fornecedores */}
      <Section $delay={200} ref={listRef}>
        <SectionHeader><h2>Fornecedores</h2></SectionHeader>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <SearchField $max="320px">
            <FiSearch />
            <input placeholder="Buscar por nome ou CNPJ" aria-label="Buscar fornecedor" value={q} onChange={(e) => { setQ(e.target.value); setVisible(STEP); }} />
            {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
          </SearchField>
          <div style={{ flex: 1 }} />
          <SegmentedControl
            small
            value={sort}
            onChange={(v) => { setSort(v); setVisible(STEP); }}
            options={[{ key: "missing", label: "Mais faltantes" }, { key: "pct", label: "Menor cobertura" }, { key: "name", label: "Nome" }]}
          />
        </div>
        <FilterRow>
          <ChipScroll>
            <Chip $active={!band} onClick={() => { setBand(""); setVisible(STEP); }}>Todos <span style={{ opacity: 0.6, marginLeft: 4 }}>{formatCount(nSup)}</span></Chip>
            <Chip $active={band === "pending"} $color="#ef4444" onClick={() => { setBand("pending"); setVisible(STEP); }}>Com pendência <span style={{ opacity: 0.6, marginLeft: 4 }}>{formatCount(a.withMissing)}</span></Chip>
            {BANDS.map((b) => {
              const n = a.bands.find((x) => x.key === b.key).count;
              return n > 0 && (
                <Chip key={b.key} $active={band === b.key} $color={b.color} onClick={() => { setBand(b.key); setVisible(STEP); }}>
                  {b.label} <span style={{ opacity: 0.6, marginLeft: 4 }}>{formatCount(n)}</span>
                </Chip>
              );
            })}
          </ChipScroll>
          <SegmentedControl small value={view} onChange={(v) => { setView(v); saveView(v); }} options={VIEW_OPTIONS} />
        </FilterRow>

        {failed && (
          <Group style={{ marginTop: 14 }}>
            <Empty>
              <div className="e-icon"><FiAlertTriangle /></div>
              <div className="e-title">Não foi possível carregar os fornecedores</div>
              <div className="e-sub" style={{ marginBottom: 14 }}>Verifique a conexão e tente de novo.</div>
              <Capsule type="button" onClick={load} style={{ margin: "0 auto" }}>Tentar de novo</Capsule>
            </Empty>
          </Group>
        )}
        {loading && view === "cards" && <SupplierGrid style={{ marginTop: 14 }}>{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} $h={168} $r={18} />)}</SupplierGrid>}
        {loading && view === "list" && <div style={{ display: "grid", gap: 8, marginTop: 14 }}>{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} $h={54} $r={12} />)}</div>}
        {!loading && !failed && list.length === 0 && (
          <Group style={{ marginTop: 14 }}>
            <Empty>
              <div className="e-icon"><FiSearch /></div>
              <div className="e-title">Nenhum fornecedor</div>
              <div className="e-sub">{q ? `Nada encontrado para "${q}".` : "Nenhum fornecedor nesta faixa."}</div>
            </Empty>
          </Group>
        )}
        {!loading && list.length > 0 && view === "list" && (
          <SupplierRows>
            <div className="hd"><span>Fornecedor</span><span className="num">Com XML</span><span className="num">Documentos</span><span className="num">Faltantes</span><span /></div>
            {list.slice(0, visible).map((s) => <SupplierRow key={s.key} s={s} onOpen={openSupplierRow} />)}
          </SupplierRows>
        )}
        {!loading && list.length > 0 && view === "cards" && (
          <SupplierGrid style={{ marginTop: 14 }}>
            {list.slice(0, visible).map((s, i) => <SupplierCard key={s.key} s={s} delay={Math.min(i % STEP, 11) * 25} onOpen={(x) => onOpenSupplier(x.raw)} />)}
          </SupplierGrid>
        )}
        {hasMore && (
          <div ref={endRef} style={{ padding: "18px 0", textAlign: "center", fontSize: "0.78em", color: tokens.label2(theme) }}>
            {formatCount(visible)} de {formatCount(list.length)}
          </div>
        )}
      </Section>

      {exporting && <ExportSheet reportId={reportId} missing={a.missing} onClose={() => setExporting(false)} />}
      {!a.scope.complete && (
        <NovaAnaliseSheet
          isOpen={sendingMissing}
          onClose={() => setSendingMissing(false)}
          onStart={onReprocessStart}
          onFinished={onReprocessFinished}
          completion={{
            label: periodLabel(a.rep),
            startKey: a.rep.startKey,
            endKey: a.rep.endKey,
            missing: a.scope.cells.filter((c) => !c.sent).map((c) => ({ cnpj: c.cnpj, month: c.month })),
            existing: [...new Set(a.rep.files.map((f) => String(f.cnpj || "").replace(/\D/g, "")).filter(Boolean))],
          }}
        />
      )}
    </CreditsPage>
  );
}
